<template>
  <router-link
    :to="orderLink"
    :class="[detail.statusId, 'card']"
  >
    <!--first part-->
    <section class="card-head">
      <div class="row items-center">
        <q-avatar size="32px" class="q-mr-sm logo">
          <q-img :src="detail.productStoreLogo || defaultLogo" />
        </q-avatar>
        <div class="card-head-info">
          <div class="name">{{ detail.productStoreName }}</div>
          <div class="time">{{ timeStr }}</div>
        </div>
        <!--status text-->
        <div class="card-head-tag">
          <q-chip color="#ff9e2d" size="12px" class="statusChip font-size-12">
            {{ orderStatusText }}
          </q-chip>
        </div>
      </div>
    </section>
    <!--second part-->
    <section v-if="isReady" class="card-tips">
      <div
        class="font-size-16 text-weight-bolder"
        v-html="isDeliveryOrder ? deliveryStatusDesc : $t('order.siapMsg1')"
      />
      <div
        v-if="isDeliveryOrder || isPickup"
        class="font-size-12 text-ready-other"
      >
        {{ $t(`order.${isDeliveryOrder ? 'deliveryNumber' : 'pickUpNumber'}`) }}
      </div>
      <div
        v-if="isDeliveryOrder || isPickup"
        class="font-size-24 text-weight-bolder"
      >
        {{ isDeliveryOrder ? externalId : detail.serialNumber }}
      </div>
    </section>
    <!--product part-->
    <section v-if="detail.orderItems?.length" horizontal class="card-product">
      <!--multiple product style-->
      <div class="card-product-mul">
        <template v-for="(item, index) in detail.orderItems">
          <template v-if="detail.orderItems.length <= 5">
            <q-avatar
              v-if="!(index > 4)"
              :key="item.productId"
              rounded
              size="40px"
              class="q-mr-sm"
            >
              <q-img
                :src="$ossLimit(item.originalImageUrl || defaultLogo, 150)"
                class="img-card"
              />
            </q-avatar>
          </template>
          <template v-else>
            <q-avatar
              v-if="!(index > 3)"
              :key="item.productId"
              rounded
              size="40px"
              class="q-mr-sm"
            >
              <q-img
                :src="$ossLimit(item.originalImageUrl || defaultLogo, 150)"
              />
            </q-avatar>
            <q-avatar
              v-if="index === 4"
              :key="item.productId"
              rounded
              size="40px"
              class="q-mr-sm"
            >
              <q-img
                :src="$ossLimit(item.originalImageUrl || defaultLogo, 150)"
              >
                <div
                  class="absolute-full text-subtitle2 flex flex-center content-center"
                >
                  <q-icon name="app:moreIcon" class="logo" size="20px" />
                </div>
              </q-img>
            </q-avatar>
          </template>
        </template>
      </div>
      <!--only one product style-->
      <div class="card-product-one">
        <div v-if="!(detail.orderItems.length > 1)" class="col">
          <div class="one-name" bold>{{ firstProduct.productName }}</div>
          <div class="one-modifiers">
            <template v-for="(opti, index) in firstProduct.modifiers">
              <span :key="opti.name">
                {{ opti.name }}
                <span v-if="index !== firstProduct.modifiers.length - 1">
                  /
                </span>
              </span>
            </template>
          </div>
        </div>
        <div class="one-qty text-ready-other">
          ×{{ detail.itemQuantityTotal }}
        </div>
      </div>
    </section>
    <!--footer-->
    <section bold class="card-foot text-canceled-other">
      {{ $pid(detail.grandTotal) }}
    </section>
  </router-link>
</template>

<script>
import orderMixins from 'pages/mixin/orderMixins.js';

export default { mixins: [orderMixins] };
</script>
<script setup>
import { computed, defineProps, defineExpose } from 'vue';
import { use18n, useIns } from '../../../../utils/v2_helper/common';
import { defaultLogo } from '../../../../utils/config';
import { formatDate } from '../../../../utils/base';

const app = useIns();
const { $t } = use18n();
const props = defineProps({
  orderDetail: {
    type: Object,
    default: () => ({}),
  },
});
const externalId = computed(() => props.orderDetail?.shipment?.externalId);
const detail = computed(() => {
  // 将赠品加入到商品类表中，提供页面展示
  const newDetail = { ...props.orderDetail };
  // 解决重复id问题
  const newGiftItems = newDetail.orderGiftItems.map((it) => ({
    ...it,
    productId: `promo-${it.productId}`,
  }));
  const items = (function () {
    if (newDetail?.serviceType !== 'FULL_DINE_IN') {
      return newDetail?.orderGroups?.[0]?.orderItems || [];
    }
    return newDetail?.orderGroups?.reduce(
      (pre, cur) => pre.concat(cur?.orderItems || []),
      [],
    );
  }());
  newDetail.orderItems = [...items, ...newGiftItems].slice(0, 6);
  return newDetail;
});
// AI-assisted 2026-09 START: Give pending Blu orders an entry when the legacy status map has no route.
const orderLink = computed(() => ({
  name: app.resultSubPath || (detail.value?.payments?.[0]?.paymentMethodTypeId === 'IFORTE_BLU' ? 'waiting' : app.resultSubPath),
  query: { orderId: detail.value.orderId },
}));
// AI-assisted 2026-09 END
const timeStr = computed(() =>
  formatDate(
    detail.value?.createdTime?.dbStamp,
    'dd MMM yyyy · HH:mm:ss',
    app.language,
  ),
);
const firstProduct = computed(() => detail.value.orderItems[0]);
const deliveryStatusDesc = computed(() =>
  app.orderMsgs.status?.replace(/<br\/?>/, ''),
);
const orderStatusText = computed(() => {
  const MAP = {
    ORDER_APPROVED: {
      DINE_IN: $t('history.approvedStatus'),
      PICK_UP: $t('history.approvedStatus'),
      DELIVERY: $t('history.approvedStatus'),
    },
    ORDER_PROCESSING: {
      DINE_IN: $t('history.processingStatus'),
      PICK_UP: $t('history.processingStatus'),
      DELIVERY: $t('history.processingStatus'),
    },
    ORDER_SHIPPING: {
      DINE_IN: $t('history.shippingStatus'),
      PICK_UP: $t('history.shippingStatus'),
      DELIVERY: $t('history.shippingStatus'),
    },
    ORDER_COMPLETED: {
      DINE_IN: $t('history.completedStatus'),
      PICK_UP: $t('history.completedStatus'),
      DELIVERY: $t('history.completedStatus'),
      FULL_DINE_IN: $t('history.completedStatus'),
    },
    ORDER_CANCELLED: {
      DINE_IN: $t('history.cancelledStatus'),
      PICK_UP: $t('history.cancelledStatus'),
      DELIVERY: $t('history.cancelledStatus'),
      FULL_DINE_IN: $t('history.cancelledStatus'),
    },
  };
  const { serviceType, statusId } = detail.value;
  return MAP[statusId]?.[serviceType];
});
const isDeliveryOrder = computed(() => detail.value.serviceType === 'DELIVERY');
const isPickup = computed(() => detail.value.serviceType === 'PICK_UP');
const isReady = computed(() => detail.value.statusId === 'ORDER_SHIPPING');
// expose
const serviceTypeStatus = computed(() => props.orderDetail?.serviceType);
const shipmentStatusCode = computed(
  () => props.orderDetail?.shipment?.trackingStatusCode,
);
defineExpose({
  serviceTypeStatus,
  shipmentStatusCode,
});
</script>

<style lang="scss" scoped>
.q-img[lazy='loading'] {
  /*your style here*/
  background-image: url('../../../../assets/loading/loading.gif');
  background-size: auto;
  background-repeat: no-repeat;
  background-position: center;
}
.card {
  display: block;
  background: #f3f4f6;
  margin: 6px;
  padding: 12px;
  border-radius: $border-radius-3;
  &-head {
    margin-bottom: 6px;
    &-info {
      .name {
        font-size: 12px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .time {
        font-size: 12px;
        color: #9ca3af;
      }
    }
    .col {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    &-tag {
      margin-left: auto;
    }
    .logo {
      :deep(.q-avatar__content) {
        overflow: unset !important;
        .q-img {
          overflow: unset !important;
          .q-img__image {
            border-radius: 50%;
            border: 2px solid #ffffff;
          }
        }
      }
    }
  }
  &-tips {
    text-align: right;
  }
  &-product {
    display: flex;
    align-items: center;
    &-mul {
    }
    &-one {
      display: flex;
      flex: 1;
      align-items: center;
      .one-name {
        font-size: 16px;
      }
      .one-modifiers {
        color: $font-color-gray-light;
        font-size: 12px;
      }
      .one-qty {
        margin-left: auto;
        font-size: 12px;
        color: $grayFont;
      }
    }
    .q-img__content > div {
      background: linear-gradient(
        0deg,
        rgba(0, 0, 0, 0.72),
        rgba(0, 0, 0, 0.72)
      );
    }
  }
  &-foot {
    text-align: right;
  }
  &.ORDER_APPROVED {
    .statusChip {
      background: #ff9e2d;
      color: #ffffff;
    }
  }
  &.ORDER_PROCESSING {
    .statusChip {
      background: #38bdf8;
      color: #ffffff;
    }
  }
  &.ORDER_SHIPPING {
    background: #10b981;
    color: #ffffff;
    .time {
      color: white;
    }
    .one-modifiers {
      color: white;
    }
    .text-ready-other {
      color: #a7f3d0 !important;
    }
    .statusChip {
      background: #ffffff;
      color: #10b981;
    }
  }
  &.ORDER_COMPLETED {
    .statusChip {
      background: #ffffff;
      color: $black;
    }
  }
  &.ORDER_CANCELLED {
    .statusChip {
      background: #ffffff;
      color: #d1d5db;
    }
    .text-canceled-other {
      color: #9ca3af !important;
    }
  }
}
.img-card {
  background-size: cover;
  height: 40px;
}
</style>
